import React, { createContext, useContext, useMemo, useState } from "react";
import { en } from "./en";
import { bs } from "./bs";
import { de } from "./de";
import { fr } from "./fr";
import { es } from "./es";
import { hr } from "./hr";
import { sr } from "./sr";
import {
  getLanguage,
  saveLanguage,
} from "../utils/helperFunctions/languageStorage";
import { AppLanguage } from "./supportedLanguages";

type Language = AppLanguage;
type Dictionary = typeof en;

const dictionaries: Record<Language, Dictionary> = {
  en,
  bs: bs as Dictionary,
  de: de as Dictionary,
  fr: fr as Dictionary,
  es: es as Dictionary,
  hr: hr as Dictionary,
  sr: sr as Dictionary,
};

type I18nContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, values?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

const isSupportedLanguage = (language: string | null): language is Language => {
  return (
    language === "bs" ||
    language === "en" ||
    language === "de" ||
    language === "fr" ||
    language === "es" ||
    language === "hr" ||
    language === "sr"
  );
};

const readKey = (object: any, key: string): string | undefined => {
  return key.split(".").reduce((current, part) => current?.[part], object);
};

const interpolate = (
  value: string,
  values?: Record<string, string | number>,
) => {
  if (!values) {
    return value;
  }

  return Object.entries(values).reduce((text, [key, replacement]) => {
    return text.replaceAll(`{{${key}}}`, String(replacement));
  }, value);
};

export const LanguageProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [languageState, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem("rentpilot_language");
    return isSupportedLanguage(stored) ? stored : "bs";
  });

  React.useEffect(() => {
    getLanguage().then((stored) => {
      if (isSupportedLanguage(stored)) {
        setLanguageState(stored);
      }
    });
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      language: languageState,
      setLanguage: (nextLanguage) => {
        setLanguageState(nextLanguage);
        saveLanguage(nextLanguage);
      },
      t: (key, values) => {
        const dictionary = dictionaries[languageState];
        const fallbackDictionary = dictionaries.bs;
        const found =
          readKey(dictionary, key) ??
          readKey(fallbackDictionary, key) ??
          readKey(dictionaries.en, key);

        return interpolate(found || key, values);
      },
    }),
    [languageState],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useTranslation = () => {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error("useTranslation must be used inside LanguageProvider");
  }

  return context;
};
